import { t } from '@hefe/i18n';
import {
  BasketballSwitchRequestEventType,
  BasketballSwitchRequestSwitchType,
  UserRole,
} from '@hefe/models';
import { BasketballSwitchRequestsSlice } from '@hefe/store';
import { Badge, Col, Row, Space, Spin, Tabs, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import SwitchRequestsCreateCoachGameForm from '../../forms/switch-requests-create-coach-game.form';
import SwitchRequestsCreateCoachPracticeForm from '../../forms/switch-requests-create-coach-practice.form';
import SwitchRequestsCreateRefereeGameForm from '../../forms/switch-requests-create-referee-game.form';
import SwitchRequestsCreateTaskForm from '../../forms/switch-requests-create-task.form';
import SwitchRequestsTabComponent from '../switch-requests-tab/switch-requests-tab.component';

interface SwitchRequestsComponentProps {
  userId: number;
  roles: string[];
  active: boolean;
}

export default function SwitchRequestsComponent(
  props: SwitchRequestsComponentProps
) {
  const { active, roles } = props;
  const dispatch = useAppDispatch();
  const allSwitchRequests = useSelector(
    BasketballSwitchRequestsSlice.switchRequestsSelector
  );

  const switchRequests = allSwitchRequests[props.userId];

  const [isLoading, setLoading] = useState(false);

  const fetchRequests = () => {
    setLoading(true);
    dispatch(
      BasketballSwitchRequestsSlice.fetchBasketballUserSwitchRequests(
        props.userId
      )
    )
      .unwrap()
      .catch(() => null)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (active && !switchRequests) {
      fetchRequests();
    }
  }, [active]);

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (!switchRequests) {
    return null;
  }

  const coachGamesRequests = switchRequests.filter(
    (request) =>
      request.switch_type === BasketballSwitchRequestSwitchType.COACH &&
      request.event_type === BasketballSwitchRequestEventType.GAME
  );
  const coachPracticesRequests = switchRequests.filter(
    (request) =>
      request.switch_type === BasketballSwitchRequestSwitchType.COACH &&
      request.event_type === BasketballSwitchRequestEventType.PRACTICE
  );
  const refereeRequests = switchRequests.filter(
    (request) =>
      request.switch_type === BasketballSwitchRequestSwitchType.REFEREE
  );
  const tasksRequests = switchRequests.filter(
    (request) => request.switch_type === BasketballSwitchRequestSwitchType.TASK
  );

  const renderTabTitle = (title: string, subtitle: string, count: number) => {
    return (
      <Badge count={count} offset={[10, 0]} size="small">
        <Space direction="vertical" size={0} align="start">
          <Typography.Text>{title}</Typography.Text>
          <Typography.Text style={{ fontSize: 8 }}>{subtitle}</Typography.Text>
        </Space>
      </Badge>
    );
  };

  return (
    <Row>
      <Col xs={24} lg={24}>
        <Tabs tabPosition="left" defaultActiveKey="1">
          {roles.includes(UserRole.BB_USER) && (
            <Tabs.TabPane
              key="2"
              tab={renderTabTitle(
                t('tasks.plural'),
                '',
                tasksRequests.filter((r) => r.new_user_id === null).length
              )}
            >
              <SwitchRequestsTabComponent
                requests={tasksRequests}
                userId={props.userId}
                buttons={[
                  {
                    title: t('create_request'),
                    form: (
                      <SwitchRequestsCreateTaskForm userId={props.userId} />
                    ),
                    formId: `switch-request-form-create-task-${props.userId}`,
                  },
                ]}
              />
            </Tabs.TabPane>
          )}
          {roles.includes(UserRole.BB_COACH) && (
            <Tabs.TabPane
              key="3"
              tab={renderTabTitle(
                t('coach'),
                t('games.plural'),
                coachGamesRequests.filter((r) => r.new_user_id === null).length
              )}
            >
              <SwitchRequestsTabComponent
                requests={coachGamesRequests}
                userId={props.userId}
                buttons={[
                  {
                    title: t('create_request'),
                    form: (
                      <SwitchRequestsCreateCoachGameForm
                        userId={props.userId}
                      />
                    ),
                    formId: `switch-request-form-create-coach-game-${props.userId}`,
                  },
                ]}
              />
            </Tabs.TabPane>
          )}
          {roles.includes(UserRole.BB_COACH) && (
            <Tabs.TabPane
              key="4"
              tab={renderTabTitle(
                t('coach'),
                t('practices.plural'),
                coachPracticesRequests.filter((r) => r.new_user_id === null)
                  .length
              )}
            >
              <SwitchRequestsTabComponent
                requests={coachPracticesRequests}
                userId={props.userId}
                buttons={[
                  {
                    title: t('create_request'),
                    form: (
                      <SwitchRequestsCreateCoachPracticeForm
                        userId={props.userId}
                      />
                    ),
                    formId: `switch-request-form-create-coach-practice-${props.userId}`,
                  },
                ]}
              />
            </Tabs.TabPane>
          )}
          {roles.includes(UserRole.BB_REFEREE) && (
            <Tabs.TabPane
              key="5"
              tab={renderTabTitle(
                t('referees.singular'),
                '',
                refereeRequests.filter((r) => r.new_user_id === null).length
              )}
            >
              <SwitchRequestsTabComponent
                requests={refereeRequests}
                userId={props.userId}
                buttons={[
                  {
                    title: t('create_request'),
                    form: (
                      <SwitchRequestsCreateRefereeGameForm
                        userId={props.userId}
                      />
                    ),
                    formId: `switch-request-form-create-referee-game-${props.userId}`,
                  },
                ]}
              />
            </Tabs.TabPane>
          )}
        </Tabs>
      </Col>
    </Row>
  );
}
